import { z } from "zod";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const BRAND_OPTIONS = [
  { value: "countre_dairy", label: "Countre Dairy" },
  { value: "yousra", label: "Yousra" },
  { value: "florida", label: "Florida" },
  { value: "kamax_kamix", label: "KamaX / KamiX" },
] as const;

export const BRAND_ENUM = ["countre_dairy", "yousra", "florida", "kamax_kamix"] as const;
export type BrandSlug = (typeof BRAND_ENUM)[number];

export const CATEGORY_OPTIONS = [
  { value: "dairy", label: "Produits laitiers" },
  { value: "sauce", label: "Sauces & Condiments" },
  { value: "food", label: "Conserves alimentaires" },
  { value: "cleaning", label: "Produits d'entretien" },
  { value: "rice", label: "Riz" },
  { value: "drink", label: "Boissons" },
] as const;

export const CATEGORY_ENUM = ["dairy", "sauce", "food", "cleaning", "rice", "drink"] as const;
export type CategorySlug = (typeof CATEGORY_ENUM)[number];

export const TVA_OPTIONS = [
  { value: 0.18, label: "18%" },
  { value: 0, label: "0% (exonéré)" },
] as const;

export const UNIT_OPTIONS = ["kg", "L", "pièce", "carton", "sachet", "boîte"] as const;

export const BRAND_LABEL_MAP: Readonly<Record<string, string>> = {
  countre_dairy: "Countre Dairy",
  yousra: "Yousra",
  florida: "Florida",
  kamax_kamix: "KamaX / KamiX",
};

export const CATEGORY_LABEL_MAP: Readonly<Record<string, string>> = {
  dairy: "Produits laitiers",
  sauce: "Sauces & Condiments",
  food: "Conserves alimentaires",
  cleaning: "Produits d'entretien",
  rice: "Riz",
  drink: "Boissons",
};

// ---------------------------------------------------------------------------
// Product type
// ---------------------------------------------------------------------------

export interface Product {
  readonly id: string;
  readonly name: string;
  readonly name_wo?: string;
  readonly sku: string;
  readonly brand: BrandSlug;
  readonly category: CategorySlug;
  readonly description: string;
  readonly price_fcfa: number;
  readonly tva_applicable: boolean;
  readonly tva_rate: number;
  readonly weight_kg: number;
  readonly unit: string;
  readonly min_order_qty: number;
  readonly stock: number;
  readonly image_url: string;
  readonly is_featured: boolean;
  readonly is_cieda_brand: boolean;
  readonly active: boolean;
  readonly sort_order: number;
  readonly created_at: string;
  readonly updated_at: string;
}

// ---------------------------------------------------------------------------
// Zod schemas
// ---------------------------------------------------------------------------

export const productFormSchema = z.object({
  name: z
    .string()
    .min(2, "Le nom doit contenir au moins 2 caractères")
    .max(200, "Le nom ne peut pas dépasser 200 caractères"),
  name_wo: z.string().max(200).optional().default(""),
  sku: z
    .string()
    .min(1, "Le SKU est requis")
    .max(50, "Le SKU ne peut pas dépasser 50 caractères"),
  brand: z.enum(BRAND_ENUM, {
    errorMap: () => ({ message: "Veuillez sélectionner une marque" }),
  }),
  category: z.enum(CATEGORY_ENUM, {
    errorMap: () => ({ message: "Veuillez sélectionner une catégorie" }),
  }),
  price_ht: z
    .number({ invalid_type_error: "Le prix doit être un nombre" })
    .int("Le prix doit être un nombre entier")
    .positive("Le prix doit être supérieur à 0"),
  tva_rate: z.number().min(0).max(1),
  weight_kg: z
    .number({ invalid_type_error: "Le poids doit être un nombre" })
    .positive("Le poids doit être supérieur à 0"),
  unit: z.string().min(1, "L'unité est requise").max(50),
  min_order_qty: z
    .number({ invalid_type_error: "La quantité doit être un nombre" })
    .int("La quantité doit être un nombre entier")
    .positive("La quantité doit être supérieure à 0"),
  description: z.string().max(1000).optional().default(""),
  active: z.boolean(),
  is_featured: z.boolean(),
  is_cieda_brand: z.boolean(),
  image_url: z.string().url("URL invalide").optional().or(z.literal("")),
});

export type ProductFormValues = z.infer<typeof productFormSchema>;

// ---------------------------------------------------------------------------
// CSV import schema (one row)
// ---------------------------------------------------------------------------

export const csvRowSchema = z.object({
  name: z.string().min(2),
  sku: z.string().min(1),
  brand: z.enum(BRAND_ENUM),
  category: z.enum(CATEGORY_ENUM),
  price_fcfa: z.coerce.number().int().positive(),
  unit: z.string().min(1),
  weight_kg: z.coerce.number().positive(),
  min_order_qty: z.coerce.number().int().positive().default(1),
  tva_rate: z.coerce.number().min(0).max(1).default(0.18),
  stock: z.coerce.number().int().min(0).default(0),
  active: z.coerce.boolean().default(true),
});

export type CsvRowValues = z.infer<typeof csvRowSchema>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Compute TTC price from HT price and TVA rate */
export function computePriceTTC(priceHT: number, tvaRate: number): number {
  return Math.round(priceHT * (1 + tvaRate));
}

/** Generate a SKU suggestion from brand + name */
export function suggestSKU(brand: string, name: string): string {
  const brandPrefix = brand.substring(0, 3).toUpperCase();
  const nameSlug = name
    .replace(/[^a-zA-Z0-9]/g, "")
    .substring(0, 5)
    .toUpperCase();
  const random = Math.floor(Math.random() * 900 + 100);
  return `${brandPrefix}-${nameSlug}-${random}`;
}

/** Convert form values to API payload */
export function formToApiPayload(values: ProductFormValues): Record<string, unknown> {
  return {
    name: values.name,
    description: values.description || "",
    sku: values.sku,
    brand: values.brand,
    category: values.category,
    price_fcfa: computePriceTTC(values.price_ht, values.tva_rate),
    tva_applicable: values.tva_rate > 0,
    tva_rate: values.tva_rate,
    weight_kg: values.weight_kg,
    unit: values.unit,
    min_order_qty: values.min_order_qty,
    image_url: values.image_url || "",
    is_featured: values.is_featured,
    is_cieda_brand: values.is_cieda_brand,
    active: values.active,
  };
}

/** Convert an API Product to form values for editing */
export function productToFormValues(product: Product): ProductFormValues {
  const tvaRate = product.tva_rate ?? 0.18;
  const priceHT = tvaRate > 0
    ? Math.round(product.price_fcfa / (1 + tvaRate))
    : product.price_fcfa;

  return {
    name: product.name,
    name_wo: product.name_wo ?? "",
    sku: product.sku,
    brand: product.brand,
    category: product.category,
    price_ht: priceHT,
    tva_rate: tvaRate,
    weight_kg: product.weight_kg,
    unit: product.unit,
    min_order_qty: product.min_order_qty,
    description: product.description ?? "",
    active: product.active,
    is_featured: product.is_featured,
    is_cieda_brand: product.is_cieda_brand ?? false,
    image_url: product.image_url ?? "",
  };
}
